import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { THEME_ATTRIBUTE, type ThemeSetting } from '@insightai/tokens'

const COOKIE = 'iai-theme'
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365

interface ThemeContextValue {
  theme: ThemeSetting
  setTheme: (theme: ThemeSetting) => void
  /** The theme actually applied once `system` is resolved. */
  resolved: 'dark' | 'light'
}

const ThemeContext = createContext<ThemeContextValue | null>(null)

export function useTheme() {
  const ctx = useContext(ThemeContext)
  if (!ctx) throw new Error('useTheme must be used inside <ThemeProvider>')
  return ctx
}

function readCookie(): ThemeSetting | null {
  if (typeof document === 'undefined') return null
  const match = document.cookie.match(new RegExp(`(?:^|; )${COOKIE}=(dark|light|system)`))
  return (match?.[1] as ThemeSetting | undefined) ?? null
}

/**
 * F0-65 / DB-FR-008 — theme switching.
 *
 * The preference is stored in a plain cookie, not localStorage, for two
 * reasons: the ESLint rule banning web storage (SEC-FR-001 defence in depth),
 * and because the marketing site is prerendered — a cookie is readable by the
 * server/edge, so the correct theme can be written into the initial HTML instead
 * of flashing the wrong one. The authoritative per-user setting still lives on
 * the server for signed-in users; this is the pre-auth and offline fallback.
 */
export function ThemeProvider({
  children,
  defaultTheme = 'dark',
  onPersist,
}: {
  children: ReactNode
  defaultTheme?: ThemeSetting
  /** Called so the console can also persist to the user profile (DB-FR-008). */
  onPersist?: (theme: ThemeSetting) => void
}) {
  const [theme, setThemeState] = useState<ThemeSetting>(() => readCookie() ?? defaultTheme)
  const [systemPrefersDark, setSystemPrefersDark] = useState(() =>
    typeof window === 'undefined' ? true : !window.matchMedia('(prefers-color-scheme: light)').matches,
  )

  useEffect(() => {
    const query = window.matchMedia('(prefers-color-scheme: light)')
    const onChange = (e: MediaQueryListEvent) => setSystemPrefersDark(!e.matches)
    query.addEventListener('change', onChange)
    return () => query.removeEventListener('change', onChange)
  }, [])

  const resolved: 'dark' | 'light' =
    theme === 'system' ? (systemPrefersDark ? 'dark' : 'light') : theme

  useEffect(() => {
    document.documentElement.setAttribute(THEME_ATTRIBUTE, theme === 'system' ? 'system' : theme)
    // Keeps native form controls and scrollbars in step with the token ramp.
    document.documentElement.style.colorScheme = resolved
  }, [theme, resolved])

  const setTheme = useCallback(
    (next: ThemeSetting) => {
      setThemeState(next)
      document.cookie = `${COOKIE}=${next}; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax`
      onPersist?.(next)
    },
    [onPersist],
  )

  const value = useMemo(() => ({ theme, setTheme, resolved }), [theme, setTheme, resolved])
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}
